/**
 * Shared search-field definitions for audit_events queries.
 *
 * Used by:
 *   - src/services/auditEvents.js          (listAuditEvents + countAuditEvents)
 *   - src/hooks/communications/useCommunicationsData.js
 *   - src/pages/ActivityLog.js             (live-mode client filter)
 *
 * Keep SEARCH_PAYLOAD_KEYS curated — every key here must be a plain string
 * value surfaced in buildPayloadDetailRows.js. Exclude UUIDs, raw ids,
 * timestamps, booleans, counts, batch_id.
 *
 * Comma is the PostgREST .or() separator — no key may contain a comma.
 */

/** Top-level columns on the audit_events row (not inside payload). */
export const SEARCH_COLUMNS = ['actor_name', 'event_type'];

/**
 * Human-readable string keys from event.payload that are rendered in the
 * expanded-detail view (buildPayloadDetailRows).
 *
 * Key          Why included
 * -----------  -------------------------------------------------------
 * customer_name  Denormalized on every customer/job/invoice/estimate event.
 * worker_name    Denormalized on job/no_service/route_override/schedule events.
 * invoice_number Invoice identifier (numeric, but ilike matches it).
 * estimate_number Estimate identifier.
 * employee_name  Denormalized on employee events.
 * total          Invoice/estimate total (stored as number-string in payload).
 * recipient_email Email address on sent/reminder_sent invoice & estimate events.
 * recipient      Email address on email_* webhook events and send_failed.
 * email          Email on auth events (login, invite) and invited employee.
 * customer_email Customer email shown on customer-entity events.
 * employee_email Employee email shown on employee-entity events.
 * name           Catalog item name shown on catalog events.
 * reason         Shown on no_service, route_override, schedule, voided, backdate, bulk_op events.
 * reason_other   Free-text void reason details.
 * error_message  Error text shown on send_failed events.
 * error_summary  Error summary shown on bulk_op events.
 * bounce_message Bounce detail shown on email_bounced events.
 * failed_reason  Provider failure detail on email_failed events (KAN-683).
 * source_worker_name  Route-override "from worker" detail.
 * target_worker_name  Route-override "to worker" detail.
 * status         Status field on created invoice/estimate events.
 * invoice_status Current invoice status shown on invoice events.
 * estimate_status Current estimate status shown on estimate events.
 * lifecycle_status Lifecycle on created-customer events.
 * current_lifecycle Lifecycle shown on customer-entity events.
 * current_role   Current role shown on employee-entity events.
 * notes          Notes field on lifecycle_changed events.
 * subject        Email subject shown on email_* events.
 */
export const SEARCH_PAYLOAD_KEYS = [
  'customer_name',
  'worker_name',
  'invoice_number',
  'estimate_number',
  'employee_name',
  'total',
  'recipient_email',
  'recipient',
  'email',
  'customer_email',
  'employee_email',
  'name',
  'reason',
  'reason_other',
  'error_message',
  'error_summary',
  'bounce_message',
  'failed_reason',
  'source_worker_name',
  'target_worker_name',
  'status',
  'invoice_status',
  'estimate_status',
  'lifecycle_status',
  'current_lifecycle',
  'current_role',
  'notes',
  'subject',
];

/**
 * Build the PostgREST .or() filter string for audit_events full-text search.
 *
 * @param {string} rawSearch  Raw (un-trimmed) search string from the user.
 * @returns {string} PostgREST or-filter string, or '' if input is blank.
 */
export function buildAuditSearchOr(rawSearch) {
  const trimmed = (rawSearch || '').trim();
  if (!trimmed) return '';

  // Escape % and , which are special in PostgREST ilike / .or() syntax.
  const escaped = trimmed.replace(/[%,]/g, '\\$&');

  const columnClauses = SEARCH_COLUMNS.map((col) => `${col}.ilike.%${escaped}%`);
  const payloadClauses = SEARCH_PAYLOAD_KEYS.map((key) => `payload->>${key}.ilike.%${escaped}%`);

  return [...columnClauses, ...payloadClauses].join(',');
}

/**
 * Build a lowercased haystack string for client-side live-mode filtering
 * in ActivityLog. Joins top-level fields + every searchable payload key.
 *
 * @param {object} row  An audit_events row (actor_name, event_type, payload).
 * @returns {string}
 */
export function buildSearchHaystack(row) {
  const p = row.payload || {};
  const parts = [
    row.actor_name || '',
    row.event_type || '',
    ...SEARCH_PAYLOAD_KEYS.map((key) => (p[key] != null ? String(p[key]) : '')),
  ];
  return parts.join(' ').toLowerCase();
}
